import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { insertClientSchema } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatEINInputValue } from "@/lib/ein";
import { AddressFields } from "@/components/forms/AddressFields";
import { composeAddressLine, defaultCountry, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import { Client, Employee } from "@shared/schema";
import { SIC_CODES, formatSicCode, resolveSicCodeId } from "@shared/sicCodes";
import { clientSinceInputValue } from "@/lib/clientSince";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { ENTITY_TYPES } from "@shared/entityTypes";

interface EditClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  employees: Employee[];
  clientId: string;
}

const EditClientDialog = ({
  open,
  onOpenChange,
  client,
  employees,
  clientId,
}: EditClientDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sicOpen, setSicOpen] = useState(false);

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(employees);
  }, [employees]);

  // Custom schema for editing clients - override date fields to accept strings from form inputs
  const editClientSchema = insertClientSchema.extend({
    sicCodeId: (insertClientSchema.shape as any).sicCodeId || undefined,
    clientSince: z.string().optional().nullable(),
  });

  const editClientForm = useForm({
    resolver: zodResolver(editClientSchema),
    defaultValues: {
      name: "",
      primaryContactName: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      address: "",
      ...emptyAddressFields(),
      status: "active",
      ein: "",
      sic: "",
      sicCodeId: client?.sicCodeId || undefined,
      entityType: "",
      clientSince: "",
    },
  });

  // Use static SIC codes array
  const sicOptions = SIC_CODES.map(sic => ({
    id: sic.code, // Use code as id for compatibility
    code: sic.code,
    office: sic.office,
    industryTitle: sic.industryTitle,
  }));

  const updateClientMutation = useMutation({
    mutationFn: async (data: any) => {
      // Handle "none" value for primary contact name
      const primaryContactName =
        data.primaryContactName === "none" ? null : data.primaryContactName;
      // Set legacy sic text from selected code if user chose one
      const legacySic = data.sic || data.sicCodeId ? data.sic : client.sic;
      // Convert clientSince string to Date or null
      const clientSince = data.clientSince 
        ? new Date(data.clientSince + 'T00:00:00') 
        : null;
      const updateData = {
        ...data,
        sic: legacySic,
        primaryContactName,
        clientSince,
      } as any;
      const response = await apiRequest(
        "PATCH",
        `/api/clients/${clientId}`,
        updateData
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"], refetchType: 'all' });
      queryClient.refetchQueries({ queryKey: ["/api/clients", clientId] });
      onOpenChange(false);
      toast({ title: "Client updated successfully", variant: "success" });
    },
    onError: (error) => {
      console.error("Client update error:", error);
      console.error("Form state:", editClientForm.formState);
      console.error("Form errors:", editClientForm.formState.errors);
      toast({ title: "Failed to update client", variant: "destructive" });
    },
  });

  // Populate edit form when client data loads

const composeFullAddress = (values: Record<string, unknown>) => {
  const parts = [
    values.address1,
    values.address2,
    [values.city, values.state].filter(Boolean).join(', '),
    values.zipCode,
  ].filter((segment) => {
    if (typeof segment === 'string') {
      return segment.trim().length > 0;
    }
    return Boolean(segment);
  });
  return parts.join(', ');
};

const handleSubmitForm = (values: any) => {
  const addressLine = composeFullAddress(values);
  const payload = {
    ...values,
    address: addressLine || values.address || '',
  };
  updateClientMutation.mutate(payload);
};

  useEffect(() => {
    if (client) {
      const addressFields = normalizeAddressFields(client);
      editClientForm.reset({
        name: client.name || "",
        primaryContactName: client.primaryContactName || "none",
        contactEmail: client.contactEmail || "",
        contactPhone: client.contactPhone || "",
        website: client.website || "",
        address: client.address || "",
        ...addressFields,
        status: client.status || "active",
        ein: client.ein || "",
        sic: client.sic || "",
        sicCodeId: resolveSicCodeId(client.sicCodeId, client.sic),
        entityType: client.entityType || "",
        clientSince: clientSinceInputValue(client as any),
      });
    }
  }, [client, employees, editClientForm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Client</DialogTitle>
        </DialogHeader>
        <Form {...editClientForm}>
          <form
            onSubmit={editClientForm.handleSubmit(handleSubmitForm)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editClientForm.control}
                name="primaryContactName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary Contact Name</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary contact" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No primary contact</SelectItem>
                        {sortedEmployees
                          .filter((emp) => emp.clientId === client?.id)
                          .map((employee) => (
                            <SelectItem
                              key={employee.id}
                              value={`${employee.firstName} ${employee.lastName}`}
                            >
                              {employee.firstName} {employee.lastName}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="contactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter contact email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editClientForm.control}
                name="contactPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter contact phone"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Website</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <AddressFields control={editClientForm.control} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="ein"
                render={({ field }) => {
                  const handleEINChange = (event: ChangeEvent<HTMLInputElement>) => {
                    const formattedValue = formatEINInputValue(event.target.value);
                    field.onChange(formattedValue);
                  };

                  return (
                    <FormItem>
                      <FormLabel>EIN (Employer Identification Number)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="XX-XXXXXXX"
                          {...field}
                          value={field.value || ""}
                          onChange={handleEINChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={editClientForm.control}
                name="sicCodeId"
                render={({ field }) => {
                  const selected = sicOptions.find(
                    (opt) => opt.id === field.value
                  );
                  const label = selected
                    ? `${formatSicCode(selected.code)} - ${selected.industryTitle}`
                    : "Select SIC code";
                  return (
                    <FormItem className="max-w-full">
                      <FormLabel>SIC Code</FormLabel>
                      <div className="w-full overflow-hidden">
                        <Popover open={sicOpen} onOpenChange={setSicOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={sicOpen}
                              className="w-full flex items-center justify-between overflow-hidden"
                            >
                              <span className="truncate flex-1 text-left">{label}</span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                        <PopoverContent
                          className="w-[--radix-popover-trigger-width] p-0"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          <Command>
                            <CommandInput placeholder="Search code or industry" />
                            <CommandEmpty>No results found.</CommandEmpty>
                            <CommandList style={{ maxHeight: '250px', overflowY: 'auto' }}>
                              <CommandGroup>
                                {sicOptions.map((opt) => (
                                  <CommandItem
                                    key={opt.id}
                                    value={`${formatSicCode(opt.code)} ${opt.industryTitle}`}
                                    onSelect={() => {
                                      field.onChange(opt.id);
                                      setSicOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        opt.id === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    {formatSicCode(opt.code)} - {opt.industryTitle}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                        </Popover>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="entityType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Entity Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select entity type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENTITY_TYPES.map((et) => (
                          <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editClientForm.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={editClientForm.control}
                name="clientSince"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Since</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateClientMutation.isPending}>
                {updateClientMutation.isPending
                  ? "Updating..."
                  : "Update Client"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditClientDialog;
